/**
 * risk-adapter.ts — 风控策略真实 DTO → mock 富集行的塑形纯函数（ADM-USR-005）
 *
 * 接真改造：真实后端返回规范化 snake_case DTO（RiskRuleResponse，见 @novel-admin/shared risk.ts），
 * 而设计稿页面（users.jsx · RiskPolicy）消费的是 mock 的 camelCase 富集行（RiskRule，见
 * ~/data/mock/types.ts）。本文件做单向塑形，让页面 0 改动、设计 100% 还原。纯函数 + 严格类型，便于单测。
 *
 * 字段映射要点（DTO → mock）：
 *   action(v1 ban / v2 auto_ban / observe) → action(中文「自动封禁 / 风控观察」)  —— RISK_ACTION_ZH 映射
 *   level(high/medium/low)   → level（透传，页面按 high/medium/low 分组统计）
 *   last_hit_at(ISO|null)    → lastHit（取日期时间串；null → 空串，页面只读展示）
 *   updated_at(ISO)          → updatedAt（透传 ISO，页面 mono 展示）
 *   updated_by(string|null)  → updatedBy（null → 空串）
 *   hits / name / condition / enabled / id → 同名透传
 *   condition_dimension / condition_operator / condition_value → conditionDimension /
 *     conditionOperator / conditionValue（三列透传，阈值编辑弹窗判定可编辑性 + 组 PATCH body 用）
 */

import type {
  RiskConditionValue,
  RiskRuleDimension,
  RiskRuleOperator,
  RiskRuleResponse,
  RiskRuleV1Response,
} from '@novel-admin/shared';
import type { RiskRule } from '../mock/types';

/**
 * 页面消费的风控规则行 = mock 富集行 + 条件三列（真 DTO 独有）。
 * 三列驱动阈值编辑弹窗：仅 operator='gt' 的阈值类标量可改（契约 §③）。
 */
export type RiskRuleRow = RiskRule & {
  conditionDimension: RiskRuleDimension;
  conditionOperator: RiskRuleOperator;
  conditionValue: RiskConditionValue;
};

/** 执行动作机器码 → 页面中文标签（页面按 r.action === '自动封禁' 分支渲染 chip）。 */
const RISK_ACTION_ZH: Record<RiskRuleResponse['action'] | RiskRuleV1Response['action'], string> = {
  auto_ban: '自动封禁',
  ban: '自动封禁',
  observe: '风控观察',
};

/** 未知 action 兜底为「风控观察」（更保守的处置，避免误显示成封禁）。 */
function toRiskActionZh(action: string): string {
  return RISK_ACTION_ZH[action as keyof typeof RISK_ACTION_ZH] ?? '风控观察';
}

/** RiskRuleResponse → 页面行（last_hit_at / updated_by 为 null 时落空串，页面只读不崩）。 */
export function adaptRiskRule(dto: RiskRuleResponse | RiskRuleV1Response): RiskRuleRow {
  return {
    id: dto.id,
    name: dto.name,
    condition: dto.condition,
    action: toRiskActionZh(dto.action),
    level: dto.level,
    hits: dto.hits,
    lastHit: dto.last_hit_at ?? '',
    enabled: dto.enabled,
    updatedAt: dto.updated_at,
    updatedBy: dto.updated_by_name ?? '—',
    conditionDimension: dto.condition_dimension,
    conditionOperator: dto.condition_operator,
    conditionValue: dto.condition_value,
  };
}

/** 列表塑形便捷包装。 */
export function adaptRiskRules(list: (RiskRuleResponse | RiskRuleV1Response)[]): RiskRuleRow[] {
  return list.map(adaptRiskRule);
}
